import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { api } from '../../services/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import { ArrowLeft, Download, FileText, Printer, Sparkles, Image as ImageIcon } from 'lucide-react';

const AdminOrders = () => {
  const navigate = useNavigate();
  const { isAdmin, adminToken } = useAdmin();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to get the correct image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    // If already a full URL, return as is
    if (imageUrl.startsWith('http')) return imageUrl;
    
    // If it's a data URL (base64), return as is
    if (imageUrl.startsWith('data:')) return imageUrl;
    
    // Convert old temp-images URLs to new card-designs URLs
    if (imageUrl.includes('/temp-images/')) {
      const filename = imageUrl.split('/').pop();
      return `${BACKEND_URL}/api/card-designs/${filename}`;
    }
    
    // If it's a relative URL, prepend backend URL
    if (imageUrl.startsWith('/')) {
      return `${BACKEND_URL}${imageUrl}`;
    }
    
    // Might be just a filename/ID - try card-designs endpoint
    return `${BACKEND_URL}/api/card-designs/${imageUrl}`;
  };

  // Placeholder for missing images
  const ImageWithFallback = ({ src, alt, className }) => {
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    
    if (error || !src) {
      return (
        <div className={`${className} bg-gray-100 flex items-center justify-center text-gray-400`}>
          <div className="text-center p-2">
            <ImageIcon className="w-8 h-8 mx-auto mb-1" />
            <span className="text-xs">Image not available</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="relative">
        {loading && (
          <div className={`${className} bg-gray-100 flex items-center justify-center absolute inset-0`}>
            <div className="animate-spin w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full"></div>
          </div>
        )}
        <img 
          src={src} 
          alt={alt} 
          className={className}
          onError={() => setError(true)}
          onLoad={() => setLoading(false)}
        />
      </div>
    );
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [isAdmin, adminToken, navigate]);

  const fetchOrders = async () => {
    try {
      const data = await api.adminGetOrders(adminToken);
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await api.adminUpdateOrderStatus(orderId, newStatus, adminToken);
      toast({ title: 'Order status updated successfully!' });
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update order status',
        variant: 'destructive'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processing':
        return 'bg-blue-100 text-blue-800';
      case 'Shipped':
        return 'bg-purple-100 text-purple-800';
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const downloadCustomDesigns = async (orderId, orderNumber) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/designs`, {
        headers: { 'X-Admin-Token': adminToken }
      });
      
      const designs = await response.json();
      
      if (!designs || designs.length === 0) {
        toast({
          title: 'No custom designs',
          description: 'This order does not contain any custom designs.',
          variant: 'destructive'
        });
        return;
      }

      // Download each design
      designs.forEach((design, index) => {
        const link = document.createElement('a');
        
        // Handle both old format (imageData) and new format (imageUrl)
        let downloadUrl = design.imageData || design.imageUrl;
        
        if (downloadUrl) {
          // If it's a relative URL, prepend backend URL
          if (downloadUrl.startsWith('/')) {
            downloadUrl = `${BACKEND_URL}${downloadUrl}`;
          }
          
          link.href = downloadUrl;
          link.download = `order-${orderNumber}-design-${index + 1}-${design.product_name?.replace(/[^a-z0-9]/gi, '_') || 'card'}.png`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
      
      toast({
        title: 'Designs downloaded!',
        description: `Downloaded ${designs.length} design(s) for order ${orderNumber}`
      });
    } catch (error) {
      console.error('Error downloading designs:', error);
      toast({
        title: 'Error',
        description: 'Failed to download designs',
        variant: 'destructive'
      });
    }
  };

  const downloadEnvelopePDF = async (templateId, orderNumber) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const pdfUrl = `${BACKEND_URL}/api/templates/${templateId}/pdf`;
      
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `envelope-${orderNumber}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Envelope PDF downloaded!',
        description: 'Ready for printing.',
      });
    } catch (error) {
      console.error('Error downloading envelope:', error);
      toast({
        title: 'Error',
        description: 'Failed to download envelope PDF',
        variant: 'destructive'
      });
    }
  };

  const downloadOreoCookiePDF = async (orderId, orderNumber) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const pdfUrl = `${BACKEND_URL}/api/admin/orders/${orderId}/oreo-cookie-pdf`;
      
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `oreo-cookies-order-${orderNumber}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Oreo Cookie PDF downloaded!',
        description: 'Ready for printing on cookies.',
      });
    } catch (error) {
      console.error('Error downloading Oreo PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to download Oreo cookie PDF',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const handleRecoverOrders = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/recover-orders`, {
        method: 'POST',
        headers: { 'X-Admin-Token': adminToken }
      });
      
      const result = await response.json();
      
      if (result.success) {
        if (result.recovered_count > 0) {
          toast({
            title: `Recovered ${result.recovered_count} order(s)!`,
            description: result.recovered_orders.map(o => o.order_number).join(', '),
          });
          fetchOrders(); // Refresh the orders list
        } else {
          toast({
            title: 'No orders to recover',
            description: `All ${result.already_exists_count} paid transactions already have orders.`,
          });
        }
      } else {
        throw new Error(result.detail || 'Unknown error');
      }
    } catch (error) {
      console.error('Error recovering orders:', error);
      toast({
        title: 'Error',
        description: 'Failed to recover orders: ' + error.message,
        variant: 'destructive'
      });
    }
  };

  const handleMigrateImages = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/migrate-card-images`, {
        method: 'POST',
        headers: { 'X-Admin-Token': adminToken }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: `Image Migration Complete`,
          description: `Migrated: ${result.migrated_count}, Already in DB: ${result.already_exists_count}, Failed: ${result.failed_count}`,
        });
      } else {
        throw new Error(result.detail || 'Unknown error');
      }
    } catch (error) {
      console.error('Error migrating images:', error);
      toast({
        title: 'Error',
        description: 'Failed to migrate images: ' + error.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button onClick={() => navigate('/admin')} className="mr-4">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Manage Orders</h1>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleMigrateImages}
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Migrate Card Images
              </Button>
              <Button 
                onClick={handleRecoverOrders}
                variant="outline"
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Recover Missing Orders
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {orders.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-xl text-gray-600">No orders yet</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card key={order._id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      Order #{order.order_number}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Customer: {order.customer_email}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCustomDesigns(order._id || order.id, order.order_number)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Designs
                    </Button>
                    {/* Oreo Cookie PDF Download */}
                    {order.items?.some(item => item.customization?.type === 'oreo-cookie') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadOreoCookiePDF(order._id || order.id, order.order_number)}
                        className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        🍪 Cookie PDF
                      </Button>
                    )}
                    {/* Print Envelope - tries customization addresses first, falls back to shipping */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          let fromAddress = '';
                          let toAddress = '';
                          
                          // Try to get FROM/TO from item customization
                          const itemWithAddresses = order.items?.find(item => 
                            item.customization?.from || item.customization?.to || item.customization?.templateId
                          );
                          
                          if (itemWithAddresses?.customization?.templateId) {
                            // Fetch from template if templateId exists
                            try {
                              const templateRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/${itemWithAddresses.customization.templateId}`);
                              if (templateRes.ok) {
                                const template = await templateRes.json();
                                fromAddress = template.from_address || '';
                                toAddress = template.to_address || '';
                              }
                            } catch (e) {
                              console.error('Error fetching template:', e);
                            }
                          }
                          
                          // Fall back to customization data
                          if (!fromAddress && itemWithAddresses?.customization?.from) {
                            fromAddress = itemWithAddresses.customization.from;
                          }
                          if (!toAddress && itemWithAddresses?.customization?.to) {
                            toAddress = itemWithAddresses.customization.to;
                          }
                          
                          // Final fallback to shipping address for TO
                          if (!toAddress && order.shipping_address) {
                            toAddress = [
                              `${order.shipping_address.first_name || ''} ${order.shipping_address.last_name || ''}`.trim(),
                              order.shipping_address.address,
                              `${order.shipping_address.city}, ${order.shipping_address.state} ${order.shipping_address.zip_code}`
                            ].filter(Boolean).join('\n');
                          }
                          
                          if (!fromAddress && !toAddress) {
                            toast({ title: 'No addresses', description: 'This order has no envelope or shipping addresses', variant: 'destructive' });
                            return;
                          }
                          
                          // Set default FROM if missing
                          if (!fromAddress) {
                            fromAddress = 'Edible Greetings';
                          }
                          
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-envelope-pdf`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              from_address: fromAddress,
                              to_address: toAddress,
                              order_number: order.order_number
                            })
                          });
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `envelope-${order.order_number}.pdf`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                            toast({ title: 'Envelope PDF downloaded!' });
                          } else {
                            throw new Error('Failed to generate PDF');
                          }
                        } catch (error) {
                          console.error('Error:', error);
                          toast({ title: 'Error', description: 'Failed to generate envelope', variant: 'destructive' });
                        }
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Print Envelope
                    </Button>
                    <Badge className={getStatusColor(order.status)}>
                      {order.status}
                    </Badge>
                    <Select
                      value={order.status}
                      onValueChange={(value) => handleStatusUpdate(order._id || order.id, value)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Processing">Processing</SelectItem>
                        <SelectItem value="Shipped">Shipped</SelectItem>
                        <SelectItem value="Delivered">Delivered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  {order.items.map((item, index) => (
                    <div key={index} className="py-4 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <img
                            src={getImageUrl(item.product_image || (item.customization?.design_image_base64 || item.customization?.designImage)) || 'https://via.placeholder.com/80?text=Card'}
                            alt={item.product_name}
                            className="w-20 h-20 object-cover rounded"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/80?text=Card'; }}
                          />
                          <div>
                            <p className="font-medium text-gray-900">{item.product_name}</p>
                            <p className="text-sm text-gray-600">Qty: {item.quantity} × ${item.price?.toFixed(2)}</p>
                            {item.customization?.arEnabled && (
                              <Badge className="mt-1 bg-purple-100 text-purple-800">AR Video +$2.00</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900 mb-2">
                            ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Download Buttons Row */}
                      <div className="mt-4 flex flex-wrap gap-2">
                        {/* Download Card Design */}
                        {(item.customization?.design_image_base64 || item.customization?.designImage) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const imageUrl = getImageUrl(item.customization.design_image_base64 || item.customization.designImage);
                                const response = await fetch(imageUrl);
                                if (!response.ok) {
                                  toast({
                                    title: 'Image not found',
                                    description: 'This card image is no longer available.',
                                    variant: 'destructive'
                                  });
                                  return;
                                }
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `card-design-${order.order_number}.png`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (error) {
                                toast({
                                  title: 'Download failed',
                                  description: 'Could not download the card image.',
                                  variant: 'destructive'
                                });
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download Card
                          </Button>
                        )}
                        
                        {/* Exact-size print PDF of the card design */}
                        {item.customization?.design_image_base64 && (
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="download-card-pdf-btn"
                            onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/design-pdf`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ image_data: item.customization.design_image_base64, order_number: order.order_number })
                                });
                                if (!response.ok) throw new Error('PDF failed');
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `card-${order.order_number}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (error) {
                                toast({ title: 'Download failed', description: 'Could not generate the card PDF.', variant: 'destructive' });
                              }
                            }}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Card PDF
                          </Button>
                        )}
                        
                        {/* Download Envelope PDF */}
                        {item.customization?.templateId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadEnvelopePDF(item.customization.templateId, order.order_number)}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Envelope PDF
                          </Button>
                        )}
                        
                        {/* Generate Envelope PDF from addresses (fallback) */}
                        {!item.customization?.templateId && item.customization?.from && item.customization?.to && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-envelope-pdf`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    from_address: item.customization.from,
                                    to_address: item.customization.to,
                                    order_number: order.order_number
                                  })
                                });
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `envelope-${order.order_number}.pdf`;
                                  link.click();
                                  window.URL.revokeObjectURL(url);
                                }
                              } catch (error) {
                                console.error('Error generating envelope:', error);
                              }
                            }}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Generate Envelope
                          </Button>
                        )}
                        
                        {/* View AR Video */}
                        {item.customization?.arGreetingId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/ar/${item.customization.arGreetingId}`, '_blank')}
                          >
                            <Sparkles className="h-4 w-4 mr-1" />
                            View AR
                          </Button>
                        )}
                      </div>
                      
                      {/* Envelope Addresses for Printing */}
                      {item.customization && (item.customization.from || item.customization.to) && (
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center mb-2">
                            <Printer className="h-4 w-4 text-yellow-700 mr-2" />
                            <span className="font-medium text-yellow-800">Envelope Addresses (For Printing)</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-gray-700">FROM:</p>
                              <pre className="whitespace-pre-wrap text-gray-600 font-sans">{item.customization.from || 'N/A'}</pre>
                            </div>
                            <div>
                              <p className="font-medium text-gray-700">TO:</p>
                              <pre className="whitespace-pre-wrap text-gray-600 font-sans">{item.customization.to || 'N/A'}</pre>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Card Design Preview */}
                      {(item.customization?.design_image_base64 || item.customization?.designImage) && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="font-medium text-blue-800 mb-2">Card Design Preview:</p>
                          <img 
                            src={getImageUrl(item.customization.design_image_base64 || item.customization.designImage)} 
                            alt="Card Design" 
                            className="max-w-md rounded border border-blue-300"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 mt-4 pt-4">
                  <div className="flex justify-between items-start">
                    <div className="text-sm text-gray-600">
                      <p className="font-medium text-gray-900 mb-1">Shipping Address:</p>
                      {order.shipping_address ? (
                        <>
                          <p>{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
                          <p>{order.shipping_address.address}</p>
                          <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                        </>
                      ) : (
                        <p className="text-gray-400 italic">No shipping address provided</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Subtotal: ${(order.subtotal || 0).toFixed(2)}</p>
                      <p className="text-sm text-gray-600">Shipping: ${(order.shipping_cost || 0).toFixed(2)}</p>
                      <p className="text-lg font-medium text-gray-900 mt-2">Total: ${(order.total || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
